import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export type CentroControlPayload = Record<string, unknown>;
type SipSyncFn = (
  inCall: boolean,
  ringing: boolean,
  refresh?: boolean
) => Promise<{ payload?: CentroControlPayload | null; lineStatus?: string; remoteEnded?: boolean } | CentroControlPayload | null>;
type LineEventFn = (event: string | Record<string, unknown>) => Promise<void>;
type ControlStatusFn = (
  inCall: boolean,
  ringing: boolean
) => Promise<CentroControlPayload | null>;

@Injectable({ providedIn: 'root' })
export class CentroControlWsService {
  private ws: WebSocket | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private cfg: {
    agentId: string;
    extension: string;
    template: CentroControlPayload;
    sipSync?: SipSyncFn;
    lineEvent?: LineEventFn;
    controlStatus?: ControlStatusFn;
  } | null = null;
  private payloadTemplate: CentroControlPayload = { type: 'agentstatus' };
  private sipInCall = false;
  private sipRinging = false;
  private phone = '';
  private wsStatusOverride = '';
  private hangupPulse = false;
  private lastSipInCall = false;
  private lastSipRinging = false;
  private heartbeatMs = 2000;
  private connectSettled = false;

  readonly lineState$ = new Subject<string>();

  isConnected(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  async connect(
    wsUrl: string,
    agentId: string,
    extension: string,
    options: {
      template?: CentroControlPayload;
      sipSync?: SipSyncFn;
      lineEvent?: LineEventFn;
      controlStatus?: ControlStatusFn;
    } = {}
  ): Promise<void> {
    const url = String(wsUrl || '').trim();
    const name = String(agentId || '').trim();
    const exten = String(extension || '').trim();
    if (!url || !name || !exten) {
      throw new Error('Centro control WS config incomplete');
    }
    this.disconnect();
    this.payloadTemplate = options.template ? { ...options.template } : { type: 'agentstatus' };
    this.cfg = {
      agentId: name,
      extension: exten,
      template: this.payloadTemplate,
      sipSync: options.sipSync,
      lineEvent: options.lineEvent,
      controlStatus: options.controlStatus
    };
    this.connectSettled = false;
    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(url);
      this.ws = ws;
      const timeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error('Centro control websocket timeout'));
      }, 20000);
      const finish = (err?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        if (err) reject(err);
        else {
          this.connectSettled = true;
          console.log('[CentroControlWS] browser ready', url);
          this.startHeartbeat();
          resolve();
        }
      };
      ws.onopen = () => {
        ws.send(JSON.stringify({ name, exten, message: 'Hello' }));
      };
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(String(ev.data));
          if (!this.connectSettled) {
            const type = String(payload?.['type'] || '').toLowerCase();
            const msg = String(payload?.['message'] || payload?.['status'] || '').toUpperCase();
            if ((type === 'status' && msg === 'OK') || (type === 'state' && msg === 'READY')) {
              finish();
            }
            return;
          }
          this.handleIncomingMessage(payload);
        } catch (_e) {}
      };
      ws.onerror = () => finish(new Error('Centro control websocket error'));
      ws.onclose = () => {
        this.stopHeartbeat();
        if (!settled) finish(new Error('Centro control websocket closed'));
      };
    });
  }

  setSipState(inCall: boolean, ringing: boolean): void {
    const wasLive = this.sipInCall || this.sipRinging;
    const changed = inCall !== this.sipInCall || ringing !== this.sipRinging;
    this.sipInCall = inCall;
    this.sipRinging = ringing;
    console.log('[CentroControlWS] setSipState', { inCall, ringing });
    this.heartbeatMs = 2000;
    if (inCall || ringing) {
      this.hangupPulse = false;
      this.wsStatusOverride = '';
      this.startLinePoll();
    } else {
      this.stopLinePoll();
      if (wasLive) {
        this.hangupPulse = true;
      }
      this.wsStatusOverride = '';
    }
    if (!changed) return;
    void this.syncSipIfChanged();
    if (this.connectSettled) {
      this.restartHeartbeat();
    }
  }

  setPhone(phone: string): void {
    const next = String(phone || '').replace(/\D/g, '').slice(-10);
    if (next.length >= 10) {
      this.phone = next;
    }
  }

  getPhone(): string {
    return this.phone;
  }

  private emitLineState(st: string, extra: Record<string, unknown> = {}): void {
    const state = String(st || '').toUpperCase();
    if (!state) return;
    const normalized = state === 'DISCONNECTED' ? 'HANGUP' : state;
    console.log('[CentroControlWS] line-state', { incoming: state, normalized, ...extra });
    if (['HANGUP', 'HUNGUP', 'DISCONNECTED'].includes(state)) {
      if (this.sipInCall || this.sipRinging) {
        this.lineState$.next(normalized);
        void this.cfg?.lineEvent?.({ status: normalized, ...extra });
        return;
      }
      this.hangupPulse = true;
      this.wsStatusOverride = '';
    } else if (['ONCALL', 'UP', 'MUTE', 'HOLD', 'RINGING', 'DIALING', 'READY'].includes(state)) {
      this.wsStatusOverride = state === 'UP' ? 'ONCALL' : state;
      this.hangupPulse = false;
    }
    this.lineState$.next(normalized);
    void this.cfg?.lineEvent?.({ status: normalized, ...extra });
  }

  private shouldIgnoreRemoteLineState(st: string): boolean {
    const state = String(st || '').toUpperCase();
    if (!state) return true;
    if (this.sipRinging && state !== 'RINGING' && state !== 'DIALING') return true;
    if (this.sipInCall && ['HANGUP', 'HUNGUP', 'DISCONNECTED', 'READY'].includes(state)) return true;
    if (!this.sipInCall && !this.sipRinging && ['HANGUP', 'HUNGUP', 'DISCONNECTED'].includes(state)) return true;
    if (state === 'READY' && (this.sipInCall || this.sipRinging)) return true;
    return false;
  }

  private handleIncomingMessage(payload: Record<string, unknown>): void {
    const type = String(payload['type'] || '').toLowerCase();
    const msg = String(payload['message'] || payload['status'] || '').toUpperCase();
    if (payload['incstring']) {
      const incstring = String(payload['incstring'] || '').trim();
      const incstate = String(payload['incstate'] || msg || '').toUpperCase();
      console.log('[CentroControlWS] incoming-call', { incstring, incstate });
      void this.cfg?.lineEvent?.({
        status: incstate || 'RINGING',
        incstring,
        incstate: incstate || 'Ringing',
        incmode: payload['incmode'] || payload['mode'] || ''
      });
      if (['ONCALL', 'UP'].includes(incstate)) {
        this.wsStatusOverride = 'ONCALL';
        this.hangupPulse = false;
      } else if (incstate === 'RINGING' || incstate === 'DIALING') {
        this.wsStatusOverride = 'RINGING';
        this.hangupPulse = false;
      } else if (['HANGUP', 'HUNGUP', 'DISCONNECTED'].includes(incstate)) {
        if (!this.shouldIgnoreRemoteLineState(incstate)) {
          this.emitLineState(incstate === 'DISCONNECTED' ? 'HANGUP' : incstate, {
            incstring,
            incstate
          });
        }
      }
      return;
    }
    if (type === 'agentstatus' || type === 'state') {
      const st = type === 'agentstatus' ? String(payload['status'] || msg).toUpperCase() : msg;
      if (['ONCALL', 'UP'].includes(st)) {
        this.wsStatusOverride = 'ONCALL';
        this.hangupPulse = false;
      } else if (st === 'RINGING' || st === 'DIALING') {
        this.wsStatusOverride = 'RINGING';
        this.hangupPulse = false;
      } else if (st === 'READY') {
        this.wsStatusOverride = 'READY';
        this.hangupPulse = false;
      } else if (st && ['HANGUP', 'HUNGUP', 'DISCONNECTED'].includes(st) && !this.shouldIgnoreRemoteLineState(st)) {
        this.emitLineState(st === 'DISCONNECTED' ? 'HANGUP' : st);
      }
    } else if (['HANGUP', 'HUNGUP', 'DISCONNECTED'].includes(msg) && !this.shouldIgnoreRemoteLineState(msg)) {
      this.emitLineState(msg === 'DISCONNECTED' ? 'HANGUP' : msg);
    }
    const phone = String(payload['phone'] || '').replace(/\D/g, '').slice(-10);
    if (phone.length >= 10) {
      this.phone = phone;
    }
  }

  private resolveStatus(): string {
    if (this.sipRinging) return 'RINGING';
    if (this.sipInCall) {
      const override = String(this.wsStatusOverride || '').toUpperCase();
      if (['MUTE', 'HOLD'].includes(override)) return override;
      return 'ONCALL';
    }
    if (this.hangupPulse) return 'HANGUP';
    return 'READY';
  }

  private buildPayload(): CentroControlPayload {
    return {
      ...this.payloadTemplate,
      type: 'agentstatus',
      status: this.resolveStatus(),
      phone: this.phone || String(this.payloadTemplate['phone'] || '')
    };
  }

  private async syncSipIfChanged(refresh = false): Promise<void> {
    if (!this.cfg?.sipSync) return;
    if (!refresh && this.sipInCall === this.lastSipInCall && this.sipRinging === this.lastSipRinging) {
      return;
    }
    if (!refresh) {
      this.lastSipInCall = this.sipInCall;
      this.lastSipRinging = this.sipRinging;
    }
    try {
      const res = await this.cfg.sipSync(this.sipInCall, this.sipRinging, refresh);
      const payload = res && typeof res === 'object' && 'payload' in res
        ? (res as { payload?: CentroControlPayload | null }).payload
        : (res as CentroControlPayload | null);
      if (payload && typeof payload === 'object') {
        this.payloadTemplate = { ...this.payloadTemplate, ...payload };
      }
      const lineStatus = res && typeof res === 'object' && 'lineStatus' in res
        ? String((res as { lineStatus?: string }).lineStatus || '').toUpperCase()
        : '';
      const remoteEnded = !!(res && typeof res === 'object' && 'remoteEnded' in res
        && (res as { remoteEnded?: boolean }).remoteEnded);
      if (remoteEnded || ['HANGUP', 'HUNGUP'].includes(lineStatus)) {
        if (!this.shouldIgnoreRemoteLineState(lineStatus || 'HANGUP')) {
          this.emitLineState(lineStatus || 'HANGUP');
        }
      }
    } catch (_e) {}
  }

  private startLinePoll(): void {
    this.stopLinePoll();
    this.pollTimer = setInterval(() => {
      if (!this.sipInCall && !this.sipRinging) {
        this.stopLinePoll();
        return;
      }
      void this.syncSipIfChanged(true);
    }, 5000);
  }

  private stopLinePoll(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async sendHeartbeat(): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (this.cfg?.controlStatus) {
      try {
        const payload = await this.cfg.controlStatus(this.sipInCall, this.sipRinging);
        if (payload && typeof payload === 'object') {
          this.payloadTemplate = { ...this.payloadTemplate, ...payload };
        }
      } catch (_e) {}
    }
    try {
      const payload = this.buildPayload();
      this.ws.send(JSON.stringify(payload));
      console.log('[CentroControlWS] heartbeat', {
        status: String(payload['status'] || ''),
        phone: String(payload['phone'] || ''),
      });
      if (this.hangupPulse && !this.sipInCall && !this.sipRinging) {
        this.hangupPulse = false;
      }
    } catch (_e) {}
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    const tick = () => { void this.sendHeartbeat(); };
    void this.sendHeartbeat();
    this.timer = setInterval(tick, this.heartbeatMs);
  }

  private restartHeartbeat(): void {
    if (!this.connectSettled) return;
    this.startHeartbeat();
  }

  private stopHeartbeat(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  disconnect(): void {
    this.stopHeartbeat();
    this.stopLinePoll();
    this.cfg = null;
    this.connectSettled = false;
    this.sipInCall = false;
    this.sipRinging = false;
    this.lastSipInCall = false;
    this.lastSipRinging = false;
    this.phone = '';
    this.wsStatusOverride = '';
    this.hangupPulse = false;
    this.payloadTemplate = { type: 'agentstatus' };
    this.heartbeatMs = 2000;
    try {
      this.ws?.close();
    } catch (_e) {}
    this.ws = null;
  }
}
